import AppCache from '@solink/vd-data-layer/api-clients/shared/AppCache';
import { type SolinkDexie } from '@solink/vd-data-layer/db';
import { type ImpersonatorSession, type UserSession } from '@solinkcorp/authentication';
import { type LocaleName } from '@solinkcorp/locale';
import settings from '@solinkcorp/settings';
import { DEFAULT_LOCALE } from '@solinkcorp/vd-common';
import CameraActions from 'actions/CameraActions';
import { updateFieldStats } from 'actions/fieldStatsActions';
import { setFilter } from 'actions/filterActions';
import { updateLocale } from 'actions/localeActions';
import * as LocationActions from 'actions/LocationActions';
import UserActions from 'actions/UserActions';
import { PlatformApi } from 'clients';
import { AppDispatcher } from 'dispatcher';
import AuthService from 'models/AuthService';
import { getDateByMetadataProp } from 'models/authUtils';
import { type FilterState } from 'reducers/filterReducer';
import { isKiosk } from 'src/core/utils/kioskUtils';
import { AuthStore } from 'stores/AuthStore';
import { type DispatchType } from 'stores/store';
import { isValidLocale } from 'utils/LocaleUtil';
import {
  EVENTS_BLACK_OUT_WINDOW_IN_MINUTES,
  HIDE_FROM_DISPLAY_TAG,
  USER_LOGGED_IN,
} from 'vd-constants';

export function onUserSessionChange(
  db: SolinkDexie,
  dispatch: DispatchType,
  filterDate: number,
  user: UserSession,
  prevUser: UserSession | undefined,
  impersonator: ImpersonatorSession | undefined,
): void {
  AuthService.setCurrentUser(user, impersonator);

  /** Update global settings store with service URLs from the session. */
  settings.update(user.serviceMap);

  AppCache.configure(true);

  const locale = getLocale(user);

  // This will be called if the session changes, which will happen if the
  // user preferences are updated. Only re-fetch data if the user's access
  // token has changed.
  if (user.accessToken !== prevUser?.accessToken) {
    // Guests have no need for field stats right now but in the future they might
    if (user.type !== 'guest') {
      // Get all field stats from index mappings
      getFieldStats(dispatch, false, true);
    }

    UserActions.getUser();

    if (user.userId !== prevUser?.userId) {
      LocationActions.getLocations(db, user).catch((err) => {
        console.error('Error clearing/loading database after switching users.', err);
      });
    }

    if (!isKiosk()) {
      CameraActions.getCameraGroups();
      CameraActions.getLocationRepCameraGroups();
    }

    const payload = {
      token: user.accessToken,
      aws: JSON.stringify(user.aws),
      chUserMetadata: user.profile.metadata,
      chUserPreferences: user.profile.preferences,
      chUserFeatures: user.profile.features,
      locale,
    };
    AppDispatcher.dispatch(USER_LOGGED_IN, payload);
  }

  dispatch(updateLocale(locale));

  // This is a bit of a hack until there's a redux "USER_LOGGED_IN" action.
  if (user.userId !== prevUser?.userId) {
    const loginFilter = getLoginFilter(user, filterDate);
    if (Object.keys(loginFilter).length === 0) {
      dispatch(setFilter(loginFilter));
    }
  }
}

function getLocale(user: UserSession): LocaleName {
  const locale = user.profile.preferences?.locale || DEFAULT_LOCALE;
  if (!isValidLocale(locale)) {
    return DEFAULT_LOCALE;
  }
  return locale;
}

/**
 * Retrieve field stats.
 *
 * @param {boolean} [retry=false] - Re-attempt API call when no results are retrieved
 */
const getFieldStats = (dispatch: DispatchType, retry = false, isQuick = false) => {
  PlatformApi()
    .eventFieldStats({
      timeout: 60 * 1000,
      queryData: { include: HIDE_FROM_DISPLAY_TAG },
      isQuick,
      context: 'login',
    })
    .then((stats) => {
      if (stats.length === 0 && !retry) {
        getFieldStats(dispatch, true, isQuick);
        return;
      }

      dispatch(updateFieldStats(stats));
    })
    .catch((err) => {
      console.error('Error getting field stats', err);
      if (retry) {
        // We only retry once - if the retry fails, use an empty set for fieldStats,
        // so at least video-discovery-web will load.
        dispatch(updateFieldStats([]));
      }
    });
};

/**
 * Load filter details from `AuthStore.getOriginalQuery()`, if there are any.
 * The idea here is when someone navigates to a page, and is not logged in,
 * if there were query parameters in the URL that would have set up filters,
 * we stash those query parameters in local storage, then fetch them out here
 * after the login and set up the appropriate filter.
 */
function getLoginFilter(user: UserSession, filterDate: number) {
  const orig = AuthStore.getOriginalQuery();
  const { date: queryDate, locationId, siteIds, range } = orig;
  const date = queryDate ? queryDate.valueOf() : filterDate;

  const filter: Partial<FilterState> = {};

  if (date) {
    filter.date = date;
  }

  /**
   * If the locationId and locationIds are not accessbile to this user we can't catch it at this point
   *  This is because the locations list are most likely not in place yet.
   * So, the locationId and locationIds can be invalid.
   * Also, support legacy locationId param if there is no locationIds
   */
  if (siteIds?.length) {
    filter.locationIds = siteIds.slice();
  } else if (locationId) {
    filter.locationIds = [locationId];
  }

  if (range) {
    filter.range = range;
  }

  if (user.type === 'guest') {
    const minDate = getDateByMetadataProp(user, 'videoPlaybackStartsAt');

    if (minDate && date < minDate.valueOf()) {
      filter.date = Date.now() - EVENTS_BLACK_OUT_WINDOW_IN_MINUTES * 60 * 1000;
    }
  }

  return filter;
}
